import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, isValid, parseISO, subDays } from 'date-fns';

import { ArrowRightIcon } from '../../../assets/ArrowRight';
import { XIcon } from '../../../assets/X';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from '../../../components/ui/drawer';
import { Spinner } from '../../../components/Spinner';
import { DatePickerDrawer } from '../../../components/DatePickerDrawer';
import { cn } from '../../../lib/utils';
import { useBonusHistory } from '../services/useBonusHistory';

type BonusHistoryDrawerProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Стрелка «назад» — вернуться к QR; крестик просто закрывает. */
  onBack: () => void;
};

const DEFAULT_PERIOD_DAYS = 30;
const API_DATE = 'yyyy-MM-dd';
const VIEW_DATE = 'dd.MM.yyyy';

const formatView = (value: string) => {
  const parsed = parseISO(value);

  return isValid(parsed) ? format(parsed, VIEW_DATE) : value;
};

/** Календарь из макета: 13px, два зелёных. */
const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
    <rect x="0.5" y="2" width="12" height="10.5" rx="2" fill="#78b5a3" />
    <rect x="0.5" y="2" width="12" height="3.5" rx="1.5" fill="#007854" />
    <rect x="2.5" y="0.5" width="1.5" height="3" rx="0.75" fill="#007854" />
    <rect x="9" y="0.5" width="1.5" height="3" rx="0.75" fill="#007854" />
  </svg>
);

/**
 * Поле периода «С / По» из макета: пилюля 36px, подпись слева, дата и
 * календарь справа. По тапу — своя шторка с календарём в стилистике
 * приложения, а не системный пикер.
 */
const DateField = ({
  label,
  value,
  max,
  min,
  onChange,
}: {
  label: string;
  value: string;
  max?: string;
  min?: string;
  onChange: (value: string) => void;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 flex-1 items-center justify-between rounded-full bg-fill-background pl-[14px] pr-[13px]"
      >
        <span className="text-body-base-medium text-text-primary">{label}</span>
        <span className="flex items-center gap-2 text-body-base-medium text-text-tertiary">
          {formatView(value)}
          <CalendarIcon />
        </span>
      </button>
      <DatePickerDrawer
        open={open}
        setOpen={setOpen}
        title={t('select_date')}
        value={parseISO(value)}
        min={min ? parseISO(min) : undefined}
        max={max ? parseISO(max) : undefined}
        onChange={day => onChange(format(day, API_DATE))}
      />
    </>
  );
};

/** Второй экран из макета 1748-74892: операции по балансу за период. */
export const BonusHistoryDrawer = ({
  open,
  setOpen,
  onBack,
}: BonusHistoryDrawerProps) => {
  const { t } = useTranslation();
  const today = format(new Date(), API_DATE);
  const [from, setFrom] = useState(() =>
    format(subDays(new Date(), DEFAULT_PERIOD_DAYS), API_DATE)
  );
  const [to, setTo] = useState(today);

  const { operations, isLoading, isError, errorMessage, hasMore, loadMore, isLoadingMore } =
    useBonusHistory({ from, to }, open);

  // Бесконечная прокрутка: «маячок» в конце списка. Как только он показался
  // в области прокрутки шторки — тянем следующую страницу и дописываем её к
  // уже показанным. IntersectionObserver учитывает обрезку вложенным
  // скролл-контейнером, поэтому root не нужен.
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) loadMore();
      },
      { rootMargin: '120px' }
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadMore, operations.length]);

  return (
    <Drawer open={open} onOpenChange={setOpen} shouldScaleBackground={false}>
      <DrawerContent handleClassName="hidden" className="border-none bg-white">
        {/* Шапка: pad 16 сверху, стрелка 24, заголовок 18/24, крестик 24 на
            #e1e4e8; до фильтра 20. */}
        <div className="flex items-center gap-2 px-4 pb-5 pt-4">
          <button
            type="button"
            aria-label={t('back')}
            onClick={onBack}
            className="flex h-6 w-6 items-center justify-center"
          >
            <ArrowRightIcon className="h-4 w-2 rotate-180 text-text-tertiary" />
          </button>
          <DrawerTitle className="flex-1 text-display-md text-text-primary">
            {t('bonus-history-title')}
          </DrawerTitle>
          <button
            type="button"
            aria-label={t('cancel')}
            onClick={() => setOpen(false)}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-[#e1e4e8]"
          >
            <XIcon className="h-2.5 w-2.5 text-text-icon-secondary" />
          </button>
        </div>

        {/* Фильтр: пилюли 157×36, между ними тире в 29px. */}
        <div className="flex items-center px-4">
          <DateField label={t('date-from')} value={from} max={to} onChange={setFrom} />
          <span className="w-[29px] text-center text-text-tertiary">—</span>
          <DateField
            label={t('date-to')}
            value={to}
            min={from}
            max={today}
            onChange={setTo}
          />
        </div>

        {/* Список: карточки уже фильтра на 4px с каждой стороны, между ними 12. */}
        {/* Нижний отступ — чтобы последняя карточка не упиралась в край. */}
        <div className="flex flex-col gap-3 px-5 pb-8 pt-3 [padding-bottom:calc(2rem+var(--tg-safe-area-inset-bottom,0px))]">
          {isLoading && (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          )}

          {!isLoading && (isError || !operations.length) && (
            <p className="py-10 text-center text-body-sm-regular text-text-secondary">
              {isError ? errorMessage : t('history-empty')}
            </p>
          )}

          {operations.map(operation => (
            // Карточка: r16, pad 12/16/8/16; дата 18/24; пунктир через 11;
            // строки 14/20 с зазором 4.
            <div
              key={operation.id}
              className="flex flex-col rounded-2xl bg-fill-background px-4 pb-2 pt-3"
            >
              <div className="flex h-6 items-center justify-between">
                <p className="text-display-md text-text-primary">
                  {formatView(operation.date)}
                </p>
                <p
                  className={cn(
                    'flex items-center text-display-md',
                    operation.amount < 0 ? 'text-[#ff4141]' : 'text-brand-green'
                  )}
                >
                  {operation.amount > 0 ? '+' : operation.amount < 0 ? '−' : ''}
                  {Math.abs(operation.amount).toLocaleString('ru-RU')}
                  {'\u00a0'}E
                </p>
              </div>
              <div className="mb-2 mt-[11px] border-t border-dashed border-[#e1e4e8]" />
              {/* У списаний срока сгорания нет — строку не рисуем, а не
                  ставим прочерк. Филиала в ответе бэкенда нет вовсе. */}
              <div className="flex h-5 items-center justify-between text-body-sm-medium">
                <span className="text-text-secondary">{t('points-expire-on')}</span>
                <span className="text-text-primary">
                  {operation.expiresAt ? formatView(operation.expiresAt) : '—'}
                </span>
              </div>
            </div>
          ))}

          {hasMore && (
            <div ref={sentinel} className="flex h-12 items-center justify-center">
              {isLoadingMore && <Spinner />}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
